import React, { useEffect, useMemo, useRef, useState } from 'react';
import Queues from './Queues';

const API_BASE = (process.env.REACT_APP_API_BASE || 'https://api.insightos.co.uk').replace(/\/$/, '');
const STRIPE_BASE = (process.env.REACT_APP_STRIPE_WEBHOOK_BASE || 'https://stripe.insightos.co.uk').replace(/\/$/, '');
const SLIDE_TITLES = ['Overview', 'Live Sales', 'Host & Queue', 'Companies', 'Onboarding', 'Security', 'PM2 Logs'];
const CREDIT_VALUE = 0.33;

// Step configuration
const STEPS_CONFIG = [
  { number: 1, name: 'Data Processing Agreement', description: 'Review and accept data processing terms.', requiresDPA: false },
  { number: 2, name: 'Google Calendar Sync', description: 'Connect your calendar for meetings.', requiresDPA: true },
  { number: 3, name: 'Role Calibration', description: 'Set roles and expectations for your team.', requiresDPA: true },
  { number: 4, name: 'Company Policies', description: 'Define policies and compliance standards.', requiresDPA: true },
  { number: 5, name: 'Company Information', description: 'Enter your company details.', requiresDPA: true },
  { number: 6, name: 'Add Users', description: 'Invite users and assign roles.', route: '/users', requiresDPA: true },
  { number: 7, name: 'Department & Teams', description: 'Organize your team into departments and teams.', requiresDPA: true },
  { number: 8, name: 'Finance Centre', description: 'Manage and track staff costs, expenses, set milestones and project budgets.', route: '/finances', requiresDPA: true },
  { number: 9, name: 'Process & SOPs', description: 'Create and manage your processes.', route: '/process-creator', requiresDPA: true },
  { number: 10, name: 'Book Your Shifts', description: 'Set up shift scheduling and overtime.', requiresDPA: true },
  { number: 11, name: 'Set Tasks', description: 'Create tasks and assign them to your team.', route: '/taskcalendar', requiresDPA: true },
  { number: 12, name: 'Holiday', description: 'Manage holiday requests and approvals.', requiresDPA: true },
  { number: 13, name: 'Staff Reporting', description: 'View reports on staff performance and attendance.', route: '/appraisals', requiresDPA: true },
  { number: 14, name: 'Training Centre', description: 'Access and assign training courses.', route: '/training', requiresDPA: true },
  { number: 15, name: 'Customer CRM', description: 'Manage customer relationships and leads.', route: '/customercrm', requiresDPA: true },
  { number: 16, name: 'Create Your First Project', description: 'Start a new project and define its goals.', route: '/project-management', requiresDPA: true },
  { number: 17, name: 'Send your first message', description: 'Communicate with your team via instant messaging.', route: '/chatdashboard', requiresDPA: true },
  { number: 18, name: 'Create your first post', description: 'Share updates and announcements on your team feed.', route: '/posts', requiresDPA: true },
];

export default function ViewDashboard() {
  const [loginsData, setLoginsData] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [salesLeads, setSalesLeads] = useState([]);
  const [leadsTable, setLeadsTable] = useState([]);
  const [companiesData, setCompaniesData] = useState([]);
  const [serverStats, setServerStats] = useState(null);
  const [pm2Processes, setPm2Processes] = useState([]);
  const [pm2Logs, setPm2Logs] = useState([]);
  const [pm2LogsLoading, setPm2LogsLoading] = useState(false);
  const pm2LogsFetchedRef = useRef(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeSlide, setActiveSlide] = useState(0);
  const [stripeAlive, setStripeAlive] = useState(null);
  const [stripeLastEvent, setStripeLastEvent] = useState(null);
  const [showWebhookModal, setShowWebhookModal] = useState(false);
  const [webhookModalEvent, setWebhookModalEvent] = useState(null);
  const [showDefconModal, setShowDefconModal] = useState(false);
  const [defconModalEvents, setDefconModalEvents] = useState([]);

  // Auto-scroll controls
  const [autoScroll, setAutoScroll] = useState(true);
  const slideTimerRef = useRef(null);

  // New company popup
  const [newCompanies, setNewCompanies] = useState([]);
  const [showNewCompanyPopup, setShowNewCompanyPopup] = useState(false);
  const [isNewCompanyMinimized, setIsNewCompanyMinimized] = useState(false);

  // Trial Click popup
  const [trialClickLeads, setTrialClickLeads] = useState([]);
  const [showTrialClickPopup, setShowTrialClickPopup] = useState(false);
  const [isTrialClickMinimized, setIsTrialClickMinimized] = useState(false);
  const [markovData, setMarkovData] = useState(null);

  const mountedRef = useRef(true);
  const seenStripeEventIdRef = useRef(null);
  const seenDefconSignatureRef = useRef('');

  // Fetch all data
  const fetchAll = async () => {
    setLoading(true);
    setError(null);

    try {
      const [loginsResp, ticketsResp, salesResp, leadsResp, serverResp, companiesResp, markovResp] = await Promise.all([
        fetch(`${API_BASE}/admin/logins`, { credentials: 'include' }),
        fetch(`${API_BASE}/admin/tickets`, { credentials: 'include' }),
        fetch(`${API_BASE}/admin/sales-live`, { credentials: 'include' }),
        fetch(`${API_BASE}/leads/leadtable`, { credentials: 'include' }).catch(() => null),
        fetch(`${API_BASE}/admin/stats`, { credentials: 'include' }).catch(() => null),
        fetch(`${API_BASE}/admin/companies`, { credentials: 'include' }).catch(() => null),
        fetch(`${API_BASE}/admin/markov`, { credentials: 'include' }).catch(() => null),
      ]);

      const loginsJson = loginsResp.ok ? await loginsResp.json() : null;
      const ticketsJson = ticketsResp.ok ? await ticketsResp.json() : null;
      const salesJson = salesResp.ok ? await salesResp.json() : null;
      const leadsJson = leadsResp && leadsResp.ok ? await leadsResp.json() : null;
      const serverJson = serverResp && serverResp.ok ? await serverResp.json() : null;
      const companiesJson = companiesResp && companiesResp.ok ? await companiesResp.json() : null;
      const markovJson = markovResp && markovResp.ok ? await markovResp.json() : null;

      let stripeHealthJson = null;
      let stripeLastJson = null;
      try {
        const [healthResp, lastResp] = await Promise.all([
          fetch(`${STRIPE_BASE}/health`).catch(() => null),
          fetch(`${STRIPE_BASE}/stripe/last-event`).catch(() => null),
        ]);
        stripeHealthJson = healthResp && healthResp.ok ? await healthResp.json() : null;
        stripeLastJson = lastResp && lastResp.ok ? await lastResp.json() : null;
      } catch (stripeError) {
        stripeHealthJson = null;
        stripeLastJson = null;
      }

      let pm2Json = null;
      try {
        const pm2Resp = await fetch(`${STRIPE_BASE}/pm2/status`).catch(() => null);
        pm2Json = pm2Resp && pm2Resp.ok ? await pm2Resp.json() : null;
      } catch (pm2Error) {
        pm2Json = null;
      }

      if (!mountedRef.current) return;

      setLoginsData(loginsJson || null);
      setTickets(Array.isArray(ticketsJson?.data) ? ticketsJson.data : Array.isArray(ticketsJson) ? ticketsJson : []);
      setSalesLeads(Array.isArray(salesJson?.data) ? salesJson.data : Array.isArray(salesJson) ? salesJson : []);
      setLeadsTable(Array.isArray(leadsJson?.data) ? leadsJson.data : Array.isArray(leadsJson) ? leadsJson : []);
      setServerStats(serverJson?.data || null);
      setCompaniesData(Array.isArray(companiesJson?.data) ? companiesJson.data : []);
      setStripeAlive(Boolean(stripeHealthJson?.ok));
      setStripeLastEvent(stripeLastJson?.lastEvent || null);
      setPm2Processes(Array.isArray(pm2Json?.processes) ? pm2Json.processes : []);
      setMarkovData(markovJson?.data || null);

      // Check for new companies (created in last 2 hours)
      const companies = Array.isArray(companiesJson?.data) ? companiesJson.data : [];
      const twoHoursAgo = new Date(Date.now() - 2 * 60 * 60 * 1000);
      const recent = companies.filter((c) => {
        if (!c.createdAt) return false;
        const created = new Date(c.createdAt);
        return created > twoHoursAgo;
      });
      setNewCompanies(recent);
      if (recent.length > 0 && !isNewCompanyMinimized) {
        setShowNewCompanyPopup(true);
      } else if (recent.length === 0) {
        setShowNewCompanyPopup(false);
      }

      // Check for TrialClick leads (TrialClick=1 and UpdatedAt within last 10 mins)
      // FIXED: robust extraction of sales array
      const sales = Array.isArray(salesJson?.data) ? salesJson.data : (Array.isArray(salesJson) ? salesJson : []);
      const tenMinsAgo = new Date(Date.now() - 10 * 60 * 1000);
      const trialLeads = sales.filter((lead) => {
        if (Number(lead.TrialClick) !== 1) return false;
        if (!lead.UpdatedAt) return false;
        const updated = new Date(lead.UpdatedAt);
        return updated > tenMinsAgo;
      });
      // Debug log to verify
      console.log('Trial leads found:', trialLeads);
      setTrialClickLeads(trialLeads);
      if (trialLeads.length > 0 && !isTrialClickMinimized) {
        setShowTrialClickPopup(true);
      } else if (trialLeads.length === 0) {
        setShowTrialClickPopup(false);
      }

      // Webhook and Defcon modals
      if (stripeLastJson?.lastEvent?.eventId && seenStripeEventIdRef.current !== stripeLastJson.lastEvent.eventId) {
        seenStripeEventIdRef.current = stripeLastJson.lastEvent.eventId;
        setWebhookModalEvent(stripeLastJson.lastEvent);
        setShowWebhookModal(true);
      }

      const securityEvents = Array.isArray(loginsJson?.security) ? loginsJson.security : [];
      const highDefconEvents = securityEvents.filter((event) => Number(event?.DEFCON || 0) > 1);
      const signature = highDefconEvents
        .map((event) => `${event.id || event.created_at || 'x'}:${event.DEFCON}:${event.message || ''}`)
        .slice(0, 10)
        .join('|');

      setDefconModalEvents(highDefconEvents.slice(0, 10));
      if (highDefconEvents.length > 0 && signature !== seenDefconSignatureRef.current) {
        seenDefconSignatureRef.current = signature;
        setShowDefconModal(true);
      }
    } catch (err) {
      setError(String(err?.message || err));
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  };

  // Auto-scroll effect
  useEffect(() => {
    if (autoScroll) {
      slideTimerRef.current = setInterval(() => {
        setActiveSlide((current) => (current + 1) % SLIDE_TITLES.length);
      }, 60 * 1000);
    } else {
      clearInterval(slideTimerRef.current);
    }
    return () => clearInterval(slideTimerRef.current);
  }, [autoScroll]);

  // Initial fetch and refresh
  useEffect(() => {
    mountedRef.current = true;
    fetchAll();

    const refreshTimer = setInterval(fetchAll, 5 * 60 * 1000);
    return () => {
      mountedRef.current = false;
      clearInterval(refreshTimer);
    };
  }, []);

  // PM2 logs fetch only when PM2 Logs slide is active (index 6)
  useEffect(() => {
    if (activeSlide === 6) {
      pm2LogsFetchedRef.current = false;
    }
  }, [activeSlide]);

  useEffect(() => {
    if (activeSlide !== 6 || pm2LogsFetchedRef.current) return;
    pm2LogsFetchedRef.current = true;
    setPm2LogsLoading(true);
    fetch(`${STRIPE_BASE}/pm2/logs`)
      .then((r) => r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`)))
      .then((data) => setPm2Logs(Array.isArray(data.processes) ? data.processes : []))
      .catch(() => setPm2Logs([]))
      .finally(() => setPm2LogsLoading(false));
  }, [activeSlide]);

  // Computed stats
  const salesStats = useMemo(() => {
    const bySite = { avenor: { total: 0, priceClicks: 0 }, insightos: { total: 0, priceClicks: 0 } };
    (salesLeads || []).forEach((row) => {
      const site = String(row?.Site || row?.site || '').trim().toLowerCase() === 'avenor' ? 'avenor' : 'insightos';
      bySite[site].total += 1;
      if (row?.PriceClick) bySite[site].priceClicks += 1;
    });
    return bySite;
  }, [salesLeads]);

  const securityEvents = Array.isArray(loginsData?.security) ? loginsData.security : [];
  const totalLogins = (loginsData?.data || []).length || 0;
  const paidUsers = loginsData?.staff || 0;
  const ticketsOutstanding = Array.isArray(tickets) ? tickets.length : 0;
  const liveUsers = (salesLeads || []).filter((row) => Number(row?.Live) === 1).length;
  const pendingLatestReplyCount = (leadsTable || []).filter((lead) => String(lead?.LatestReply ?? '').trim().length > 0).length;

  const cpuPercent = Number(serverStats?.cpuPercent ?? serverStats?.cpu ?? 0);
  const appMemoryGB = Number((serverStats?.memory || 0) / (1024 ** 3) || serverStats?.processMemGB || 0);
  const totalMemoryGB = Number(serverStats?.totalMemGB ?? serverStats?.memoryTotal ?? 0);
  const memoryPercent = Number.isFinite(Number(serverStats?.processMemPercent))
    ? Number(serverStats.processMemPercent)
    : totalMemoryGB > 0
      ? Math.min(100, (appMemoryGB / totalMemoryGB) * 100)
      : 0;

  const companyRevenue = useMemo(() => {
    const avenorRevenue = (companiesData || []).reduce((sum, company) => sum + Number(company?.SalesCreditsGBP || 0), 0);
    const insightosRevenue = (companiesData || []).reduce((sum, company) => {
      if (Number(company?.Trial || 0) === 3) {
        return sum + Number(company?.LastPaymentAmount || 0);
      }
      return sum;
    }, 0);

    const trialCounts = (companiesData || []).reduce((acc, company) => {
      const trial = Number(company?.Trial || 0);
      if (trial === 3) acc.paying += 1;
      if (trial === 2) acc.ended += 1;
      if (trial === 1) acc.onTrial += 1;
      return acc;
    }, { paying: 0, ended: 0, onTrial: 0 });

    const endedCompanies = (companiesData || []).filter((company) => Number(company?.Trial || 0) === 2);
    const activeHighDefcon = securityEvents.filter((event) => Number(event?.DEFCON || 0) > 1);

    return {
      avenorRevenue,
      insightosRevenue,
      trialCounts,
      endedCompanies,
      activeHighDefcon,
    };
  }, [companiesData, securityEvents]);

  const topDefconEvent = useMemo(() => {
    const sorted = [...companyRevenue.activeHighDefcon].sort((a, b) => Number(b.DEFCON || 0) - Number(a.DEFCON || 0));
    return sorted[0] || null;
  }, [companyRevenue.activeHighDefcon]);

  const topDefconPreview = topDefconEvent
    ? `${topDefconEvent.DEFCON} · ${topDefconEvent.message || topDefconEvent.Message || 'Security alert'}`
    : 'No elevated DEFCON events';

  const slideLabels = SLIDE_TITLES.map((title, index) => ({ title, index }));

  // Toggle auto-scroll
  const toggleAutoScroll = () => {
    setAutoScroll((prev) => !prev);
  };

  // Popup handlers
  const minimizeNewCompanyPopup = () => {
    setIsNewCompanyMinimized(true);
    setShowNewCompanyPopup(false);
  };
  const restoreNewCompanyPopup = () => {
    setIsNewCompanyMinimized(false);
    if (newCompanies.length > 0) setShowNewCompanyPopup(true);
  };

  const minimizeTrialClickPopup = () => {
    setIsTrialClickMinimized(true);
    setShowTrialClickPopup(false);
  };
  const restoreTrialClickPopup = () => {
    setIsTrialClickMinimized(false);
    if (trialClickLeads.length > 0) setShowTrialClickPopup(true);
  };

  return (
    <>
      <div className="min-h-screen bg-[#07111f] px-3 py-4 text-white sm:px-4 lg:px-6">
        <div className="mx-auto max-w-[1600px] space-y-4">
          <section className="relative overflow-hidden rounded-[24px] border border-white/10 bg-gradient-to-br from-slate-950 via-slate-900 to-sky-950 p-4 shadow-[0_24px_80px_rgba(8,15,32,0.5)] sm:p-5">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(56,189,248,0.28),transparent_36%),radial-gradient(circle_at_bottom_left,rgba(249,115,22,0.16),transparent_34%)]" />
            <div className="relative flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center gap-3">
                <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl border border-white/15 bg-white/10 p-2 shadow-lg backdrop-blur-md sm:h-16 sm:w-16">
                  <img src="/2.png" alt="Company logo" className="h-full w-full object-contain" />
                </div>
                <div>
                  <div className="inline-flex items-center rounded-full border border-cyan-400/30 bg-cyan-400/10 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.24em] text-cyan-200 sm:text-xs">
                    Operations View
                  </div>
                  <h1 className="mt-2 text-2xl font-semibold tracking-tight text-white sm:text-4xl">Live command centre</h1>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-2 sm:justify-end">
                <button
                  onClick={toggleAutoScroll}
                  className={`rounded-full border px-3 py-1 text-xs font-semibold transition ${
                    autoScroll
                      ? 'border-emerald-400/30 bg-emerald-400/10 text-emerald-200'
                      : 'border-rose-400/30 bg-rose-400/10 text-rose-200'
                  }`}
                >
                  {autoScroll ? '⏸️ Pause' : '▶️ Resume'}
                </button>
                <div className="rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs text-slate-300">
                  Slide {activeSlide + 1} / {SLIDE_TITLES.length}
                </div>
                {slideLabels.map((slide) => (
                  <button
                    key={slide.title}
                    onClick={() => setActiveSlide(slide.index)}
                    className={`rounded-full px-3 py-1 text-xs font-semibold transition ${activeSlide === slide.index ? 'bg-cyan-500/20 text-cyan-100 border border-cyan-400/30' : 'border border-white/10 bg-white/5 text-slate-300 hover:bg-white/10'}`}
                  >
                    {slide.title}
                  </button>
                ))}
              </div>
            </div>

            <div className="mt-4 overflow-hidden rounded-[22px] border border-white/10 bg-slate-950/35">
              <div
                className="flex transition-transform duration-700 ease-in-out will-change-transform"
                style={{ transform: `translateX(-${activeSlide * 100}%)` }}
              >
                {/* Slide 0: Overview */}
                <div className="min-w-full p-4 sm:p-5">
                  <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                    <MetricCard label="Current logins" value={totalLogins} accent="from-cyan-400 to-sky-500" />
                    <MetricCard label="Live users" value={liveUsers} accent="from-emerald-400 to-teal-500" />
                    <MetricCard label="Tickets open" value={ticketsOutstanding} accent="from-rose-400 to-pink-500" />
                    <MetricCard label="Paid users" value={paidUsers} accent="from-violet-400 to-fuchsia-500" />
                  </div>

                  <div className="mt-4 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                    <Panel title="Inbound Sales - Avenor" value={salesStats.avenor.total} helper={`Price clicks ${salesStats.avenor.priceClicks}`} tone="from-amber-400/20 to-orange-500/10" />
                    <Panel title="Inbound Sales - InsightOS" value={salesStats.insightos.total} helper={`Price clicks ${salesStats.insightos.priceClicks}`} tone="from-sky-400/20 to-cyan-500/10" />
                    <Panel title="Heads up" value={pendingLatestReplyCount} helper="Leads with LatestReply" tone="from-emerald-400/20 to-teal-500/10" />
                    <Panel title="Ticket pressure" value={ticketsOutstanding} helper="Outstanding support queue" tone="from-rose-400/20 to-pink-500/10" />
                  </div>
                </div>

                {/* Slide 1: Live Sales */}
                <div className="min-w-full p-4 sm:p-5">
                  <LiveSalesTracker salesLeads={salesLeads} markovData={markovData} />
                </div>

                {/* Slide 2: Host & Queue */}
                <div className="min-w-full p-4 sm:p-5">
                  <div className="grid gap-4 xl:grid-cols-[1.15fr_0.85fr]">
                    <div className="rounded-[24px] border border-white/10 bg-white/5 p-4 shadow-[0_18px_60px_rgba(2,6,23,0.35)] backdrop-blur-xl sm:p-5">
                      <div className="flex items-center justify-between gap-3">
                        <div>
                          <div className="text-[10px] uppercase tracking-[0.24em] text-slate-400">Host snapshot</div>
                          <h2 className="mt-1.5 text-base font-semibold text-white sm:text-lg">CPU and app memory</h2>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="rounded-full border border-white/10 bg-white/5 px-3 py-1 text-[10px] text-slate-300 sm:text-xs">
                            auto refresh
                          </div>
                          <button
                            onClick={fetchAll}
                            className="rounded-full border border-cyan-400/20 bg-cyan-600/10 px-3 py-1 text-[12px] text-cyan-100 hover:bg-cyan-600/20 sm:text-sm"
                            aria-label="Refresh dashboard"
                            title="Refresh now"
                          >
                            {loading ? 'Refreshing…' : 'Refresh'}
                          </button>
                        </div>
                      </div>

                      <div className="mt-3 flex items-center justify-end">
                        <div className={`rounded-full px-3 py-1 text-xs font-semibold ${stripeAlive ? 'border border-emerald-400/30 bg-emerald-600/20 text-emerald-200' : stripeAlive === false ? 'animate-pulse border border-rose-400/30 bg-red-600/20 text-rose-200' : 'border border-white/10 bg-white/5 text-slate-300'}`}>
                          Stripe webhook: {stripeAlive === null ? 'unknown' : stripeAlive ? 'alive' : 'down'}
                        </div>
                      </div>

                      <div className="mt-4 space-y-3">
                        <StatBar label="CPU" value={cpuPercent} suffix="%" accent="bg-cyan-400" />
                        <StatBar label="Memory" value={memoryPercent} suffix="%" accent="bg-violet-400" />
                        <div className="grid grid-cols-2 gap-3 pt-1.5">
                          <CompactStat label="App RAM used" value={`${Number(appMemoryGB || 0).toFixed(2)} GB`} />
                          <CompactStat label="Total RAM" value={`${Number(totalMemoryGB || 0).toFixed(2)} GB`} />
                        </div>
                        {pm2Processes.length > 0 && (
                          <div className="mt-3 rounded-2xl border border-white/8 bg-white/3 p-3 text-sm text-slate-200">
                            <div className="mb-2 text-xs text-slate-400">PM2 processes (overview)</div>
                            <div className="flex flex-col gap-2">
                              {pm2Processes.slice(0, 5).map((process) => (
                                <div key={process.pm_id || process.name} className="flex items-center justify-between">
                                  <div className="truncate">{process.name || process.pm_id}</div>
                                  <div className={`ml-3 text-xs font-semibold ${(process.pm2_env?.status || process.status || '').toLowerCase() === 'online' ? 'text-emerald-300' : 'text-rose-300'}`}>
                                    {process.pm2_env?.status || process.status || (process.online ? 'online' : 'stopped')}
                                  </div>
                                </div>
                              ))}
                              {pm2Processes.length > 5 && <div className="mt-2 text-xs text-slate-400">+ {pm2Processes.length - 5} more</div>}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="rounded-[24px] border border-white/10 bg-gradient-to-br from-slate-900 to-slate-950 p-4 shadow-[0_18px_60px_rgba(2,6,23,0.35)] sm:p-5">
                      <div className="flex items-center justify-between gap-3">
                        <div>
                          <div className="text-[10px] uppercase tracking-[0.24em] text-slate-400">Queue health</div>
                          <h2 className="mt-1.5 text-base font-semibold text-white sm:text-lg">Active jobs</h2>
                        </div>
                        <div className="rounded-full border border-cyan-400/20 bg-cyan-400/10 px-3 py-1 text-[10px] text-cyan-100 sm:text-xs">
                          live feed
                        </div>
                      </div>
                      <div className="mt-3">
                        <Queues active={activeSlide === 2} />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Slide 3: Companies */}
                <div className="min-w-full p-4 sm:p-5">
                  <div className="grid gap-4 xl:grid-cols-[1.1fr_0.9fr]">
                    <div className="rounded-[24px] border border-white/10 bg-white/5 p-4 shadow-[0_18px_60px_rgba(2,6,23,0.35)] backdrop-blur-xl sm:p-5">
                      <div className="mb-3 flex items-center justify-between gap-3">
                        <div>
                          <div className="text-[10px] uppercase tracking-[0.24em] text-slate-400">Companies</div>
                          <h2 className="mt-1.5 text-base font-semibold text-white sm:text-lg">Revenue and trial status</h2>
                        </div>
                        <div className="rounded-full border border-white/10 bg-white/5 px-3 py-1 text-[10px] text-slate-300 sm:text-xs">
                          refreshed from admin companies
                        </div>
                      </div>

                      <div className="grid gap-3 sm:grid-cols-3">
                        <MiniStat label="Avenor revenue" value={`£${companyRevenue.avenorRevenue.toLocaleString()}`} sub="Credits converted to GBP" />
                        <MiniStat label="InsightOS revenue" value={`£${companyRevenue.insightosRevenue.toLocaleString()}`} sub="Trial = 3 paying customers" />
                        <MiniStat label="Trial ended" value={companyRevenue.trialCounts.ended} sub="Trial = 2" />
                      </div>

                      <div className="mt-3 grid gap-3 sm:grid-cols-3">
                        <MiniStat label="Paying customers" value={companyRevenue.trialCounts.paying} sub="Trial = 3" />
                        <MiniStat label="On trial" value={companyRevenue.trialCounts.onTrial} sub="Trial = 1" />
                        <MiniStat label="All companies" value={companiesData.length} sub="Loaded from Companies" />
                      </div>

                      {companyRevenue.endedCompanies.length > 0 && (
                        <div className="mt-4 rounded-2xl border border-amber-400/20 bg-amber-400/10 p-3">
                          <div className="text-xs font-semibold uppercase tracking-[0.2em] text-amber-200">Trial ended companies</div>
                          <div className="mt-2 flex flex-wrap gap-2">
                            {companyRevenue.endedCompanies.slice(0, 12).map((company) => (
                              <span key={company.CompanyCode || company.CompanyName} className="animate-pulse rounded-full border border-amber-300/40 bg-amber-300/15 px-3 py-1 text-xs text-amber-100">
                                {company.CompanyName}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      <div className="mt-4 rounded-2xl border border-white/10 bg-black/20 p-3 text-sm text-slate-200">
                        <div className="text-xs uppercase tracking-[0.2em] text-slate-400">Trial status legend</div>
                        <div className="mt-2 grid gap-2 sm:grid-cols-3">
                          <div className="rounded-xl border border-emerald-400/20 bg-emerald-400/10 px-3 py-2">Trial = 3: paying customer</div>
                          <div className="rounded-xl border border-amber-400/20 bg-amber-400/10 px-3 py-2">Trial = 2: trial ended</div>
                          <div className="rounded-xl border border-sky-400/20 bg-sky-400/10 px-3 py-2">Trial = 1: on trial</div>
                        </div>
                      </div>
                    </div>

                    <div className="rounded-[24px] border border-white/10 bg-white/5 p-4 shadow-[0_18px_60px_rgba(2,6,23,0.35)] backdrop-blur-xl sm:p-5">
                      <div className="text-[10px] uppercase tracking-[0.24em] text-slate-400">Revenue pulse</div>
                      <h2 className="mt-1.5 text-base font-semibold text-white sm:text-lg">Avenor vs InsightOS</h2>
                      <div className="mt-4 space-y-3">
                        <Panel title="Avenor revenue" value={`£${companyRevenue.avenorRevenue.toLocaleString()}`} helper={`Credits × £${CREDIT_VALUE.toFixed(2)}`} tone="from-amber-400/20 to-orange-500/10" />
                        <Panel title="InsightOS revenue" value={`£${companyRevenue.insightosRevenue.toLocaleString()}`} helper="Paying customers only" tone="from-sky-400/20 to-cyan-500/10" />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Slide 4: Onboarding */}
                <div className="min-w-full p-4 sm:p-5">
                  <OnboardingTracker companies={companiesData} />
                </div>

                {/* Slide 5: Security */}
                <div className="min-w-full p-4 sm:p-5">
                  <div className="grid gap-4 xl:grid-cols-[1fr_1fr]">
                    <div className="rounded-[24px] border border-white/10 bg-white/5 p-4 shadow-[0_18px_60px_rgba(2,6,23,0.35)] backdrop-blur-xl sm:p-5">
                      <div className="flex items-center justify-between gap-3">
                        <div>
                          <div className="text-[10px] uppercase tracking-[0.24em] text-slate-400">Security</div>
                          <h2 className="mt-1.5 text-base font-semibold text-white sm:text-lg">High DEFCON events</h2>
                        </div>
                        <div className="rounded-full border border-rose-400/20 bg-rose-400/10 px-3 py-1 text-[10px] text-rose-100 sm:text-xs">
                          alerts over DEFCON 1
                        </div>
                      </div>

                      <div className="mt-4 grid gap-3 sm:grid-cols-3">
                        <MiniStat label="DEFCON > 1" value={companyRevenue.activeHighDefcon.length} sub="Triggers modal" />
                        <MiniStat label="Max DEFCON" value={topDefconEvent ? Number(topDefconEvent.DEFCON || 0) : 0} sub="Highest event" />
                        <MiniStat label="Security events" value={securityEvents.length} sub="Loaded from admin/logins" />
                      </div>

                      <div className="mt-4 rounded-2xl border border-white/10 bg-black/20 p-3 text-sm text-slate-200">
                        <div className="text-xs uppercase tracking-[0.2em] text-slate-400">Latest high alert</div>
                        <div className="mt-2 font-semibold text-white">{topDefconPreview}</div>
                      </div>
                    </div>

                    <div className="rounded-[24px] border border-white/10 bg-white/5 p-4 shadow-[0_18px_60px_rgba(2,6,23,0.35)] backdrop-blur-xl sm:p-5">
                      <div className="text-[10px] uppercase tracking-[0.24em] text-slate-400">Queue / status</div>
                      <h2 className="mt-1.5 text-base font-semibold text-white sm:text-lg">Live operational feed</h2>
                      <div className="mt-4">
                        <Queues active={activeSlide === 5} />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Slide 6: PM2 Logs */}
                <div className="min-w-full p-4 sm:p-5">
                  <div className="rounded-[24px] border border-white/10 bg-white/5 p-4 shadow-[0_18px_60px_rgba(2,6,23,0.35)] backdrop-blur-xl sm:p-5">
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <div className="text-[10px] uppercase tracking-[0.24em] text-slate-400">PM2 Logs</div>
                        <h2 className="mt-1.5 text-base font-semibold text-white sm:text-lg">Last 300 lines per process</h2>
                      </div>
                      <button
                        onClick={() => { pm2LogsFetchedRef.current = false; setPm2LogsLoading(true); fetch(`${STRIPE_BASE}/pm2/logs`).then((r) => r.ok ? r.json() : Promise.reject()).then((d) => setPm2Logs(Array.isArray(d.processes) ? d.processes : [])).catch(() => setPm2Logs([])).finally(() => setPm2LogsLoading(false)); }}
                        disabled={pm2LogsLoading}
                        className="rounded-full border border-cyan-400/30 bg-cyan-400/10 px-4 py-1.5 text-xs font-semibold text-cyan-100 transition hover:bg-cyan-400/20 disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        {pm2LogsLoading ? 'Loading…' : 'Refresh'}
                      </button>
                    </div>
                    <div className="mt-4">
                      <Pm2Logs processes={pm2Logs} loading={pm2LogsLoading} />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {error && (
            <div className="rounded-2xl border border-rose-500/30 bg-rose-500/10 px-4 py-2.5 text-sm text-rose-100">
              {error}
            </div>
          )}
        </div>
      </div>

      {/* New Company Popup */}
      {showNewCompanyPopup && newCompanies.length > 0 && !isNewCompanyMinimized && (
        <div className="fixed bottom-6 right-6 z-50 max-w-sm rounded-2xl border border-cyan-400/30 bg-slate-950/95 p-4 shadow-2xl backdrop-blur-xl">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-2">
              <span className="text-2xl">🎉</span>
              <div>
                <h4 className="text-sm font-semibold text-white">New companies signed up!</h4>
                <p className="text-xs text-slate-300">{newCompanies.length} in the last 2 hours</p>
              </div>
            </div>
            <div className="flex gap-1">
              <button onClick={minimizeNewCompanyPopup} className="rounded-lg border border-white/10 bg-white/5 px-2 py-1 text-xs text-slate-300 hover:bg-white/10" title="Minimize">─</button>
              <button onClick={() => setShowNewCompanyPopup(false)} className="rounded-lg border border-white/10 bg-white/5 px-2 py-1 text-xs text-slate-300 hover:bg-white/10">✕</button>
            </div>
          </div>
          <div className="mt-2 max-h-32 overflow-y-auto space-y-1">
            {newCompanies.slice(0, 5).map((company) => (
              <div key={company.CompanyCode || company.id} className="text-xs text-slate-200">
                {company.CompanyName || 'Unnamed'} – {new Date(company.createdAt).toLocaleTimeString()}
              </div>
            ))}
            {newCompanies.length > 5 && <div className="text-xs text-cyan-300">+ {newCompanies.length - 5} more</div>}
          </div>
        </div>
      )}
      {isNewCompanyMinimized && newCompanies.length > 0 && (
        <div className="fixed bottom-6 right-6 z-50 cursor-pointer rounded-full border border-cyan-400/30 bg-slate-950/90 px-4 py-2 shadow-2xl backdrop-blur-xl transition hover:bg-slate-900" onClick={restoreNewCompanyPopup}>
          <span className="flex items-center gap-2 text-sm text-white">
            <span className="text-lg">🎉</span>
            {newCompanies.length} new company{newCompanies.length > 1 ? 'ies' : ''}
            <span className="text-xs text-slate-400">(click to expand)</span>
          </span>
        </div>
      )}

      {/* Trial Click Popup */}
      {showTrialClickPopup && trialClickLeads.length > 0 && !isTrialClickMinimized && (
        <div className="fixed bottom-6 left-6 z-50 max-w-sm rounded-2xl border border-emerald-400/30 bg-slate-950/95 p-4 shadow-2xl backdrop-blur-xl">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-2">
              <span className="text-2xl">🔥</span>
              <div>
                <h4 className="text-sm font-semibold text-white">Trial Click detected!</h4>
                <p className="text-xs text-slate-300">{trialClickLeads.length} in the last 10 minutes</p>
              </div>
            </div>
            <div className="flex gap-1">
              <button onClick={minimizeTrialClickPopup} className="rounded-lg border border-white/10 bg-white/5 px-2 py-1 text-xs text-slate-300 hover:bg-white/10" title="Minimize">─</button>
              <button onClick={() => setShowTrialClickPopup(false)} className="rounded-lg border border-white/10 bg-white/5 px-2 py-1 text-xs text-slate-300 hover:bg-white/10">✕</button>
            </div>
          </div>
          <div className="mt-2 max-h-32 overflow-y-auto space-y-1">
            {trialClickLeads.slice(0, 5).map((lead) => (
              <div key={lead.id} className="text-xs text-slate-200">
                {lead.CompanyName || 'Unnamed'} – {lead.Email || 'no email'} (updated {new Date(lead.UpdatedAt).toLocaleTimeString()})
              </div>
            ))}
            {trialClickLeads.length > 5 && <div className="text-xs text-emerald-300">+ {trialClickLeads.length - 5} more</div>}
          </div>
        </div>
      )}
      {isTrialClickMinimized && trialClickLeads.length > 0 && (
        <div className="fixed bottom-6 left-6 z-50 cursor-pointer rounded-full border border-emerald-400/30 bg-slate-950/90 px-4 py-2 shadow-2xl backdrop-blur-xl transition hover:bg-slate-900" onClick={restoreTrialClickPopup}>
          <span className="flex items-center gap-2 text-sm text-white">
            <span className="text-lg">🔥</span>
            {trialClickLeads.length} trial click{trialClickLeads.length > 1 ? 's' : ''}
            <span className="text-xs text-slate-400">(click to expand)</span>
          </span>
        </div>
      )}

      {/* Webhook Modal */}
      {showWebhookModal && webhookModalEvent && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
          <div className="absolute inset-0 bg-black/60" onClick={() => setShowWebhookModal(false)} />
          <div className="relative w-full max-w-lg rounded-2xl border border-white/10 bg-slate-950/95 p-5 text-white shadow-lg">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h3 className="text-lg font-semibold">Stripe Webhook Notification</h3>
                <div className="mt-1 text-sm text-slate-300">A new event was received by the webhook server.</div>
              </div>
              <button onClick={() => setShowWebhookModal(false)} className="text-slate-300">Close</button>
            </div>
            <div className="mt-4 space-y-2 text-sm text-slate-200">
              <div><strong>Event:</strong> {webhookModalEvent.eventType}</div>
              <div><strong>When:</strong> {webhookModalEvent.createdAt}</div>
              <div>
                <strong>Details:</strong>
                <pre className="mt-1 whitespace-pre-wrap rounded-xl bg-white/5 p-3 text-xs text-slate-200">{String(webhookModalEvent.details)}</pre>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Defcon Modal */}
      {showDefconModal && defconModalEvents.length > 0 && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
          <div className="absolute inset-0 bg-black/70" onClick={() => setShowDefconModal(false)} />
          <div className="relative w-full max-w-3xl rounded-3xl border border-rose-400/30 bg-slate-950 p-5 text-white shadow-[0_30px_120px_rgba(0,0,0,0.65)]">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="text-xs uppercase tracking-[0.24em] text-rose-200">Security alert</div>
                <h3 className="mt-2 text-2xl font-semibold">DEFCON above 1 detected</h3>
                <p className="mt-1 text-sm text-slate-300">These security events need attention now.</p>
              </div>
              <button onClick={() => setShowDefconModal(false)} className="rounded-full border border-white/10 bg-white/5 px-3 py-1 text-sm text-slate-200">Close</button>
            </div>

            <div className="mt-5 space-y-3 max-h-[60vh] overflow-y-auto pr-1">
              {defconModalEvents.map((event) => (
                <div key={event.id || `${event.created_at}-${event.DEFCON}`} className="rounded-2xl border border-rose-400/20 bg-rose-400/10 p-4">
                  <div className="flex flex-wrap items-center gap-2 text-sm">
                    <span className="rounded-full bg-rose-500 px-3 py-1 font-semibold text-white">DEFCON {event.DEFCON}</span>
                    <span className="text-slate-200">{event.created_at ? new Date(event.created_at).toLocaleString() : '-'}</span>
                    <span className="text-slate-400">{event.email || event.user_id || 'unknown user'}</span>
                  </div>
                  <div className="mt-2 text-sm text-slate-100">{event.message || 'No message supplied.'}</div>
                  <div className="mt-2 text-xs text-slate-400">IP: {event.ip_address || '-'}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ---------------------- Helper Components ----------------------

function MetricCard({ label, value, accent }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/10 p-3.5 shadow-lg backdrop-blur-md sm:p-4">
      <div className={`h-1.5 w-12 rounded-full bg-gradient-to-r ${accent}`} />
      <div className="mt-3 text-[10px] uppercase tracking-[0.24em] text-slate-300 sm:text-xs">{label}</div>
      <div className="mt-1.5 text-xl font-semibold text-white sm:text-2xl">{value}</div>
    </div>
  );
}

function Panel({ title, value, helper, tone }) {
  return (
    <div className={`rounded-[24px] border border-white/10 bg-gradient-to-br ${tone} p-4 shadow-[0_18px_60px_rgba(2,6,23,0.28)] backdrop-blur-xl sm:p-5`}>
      <div className="text-[10px] uppercase tracking-[0.24em] text-slate-300 sm:text-xs">{title}</div>
      <div className="mt-2 text-3xl font-semibold text-white sm:text-4xl">{value}</div>
      <div className="mt-1.5 text-xs text-slate-300 sm:text-sm">{helper}</div>
    </div>
  );
}

function StatBar({ label, value, suffix, accent }) {
  const width = `${Math.min(100, Math.max(0, value || 0))}%`;
  return (
    <div>
      <div className="flex items-center justify-between text-sm text-slate-300">
        <span>{label}</span>
        <span>{Number(value || 0).toFixed(1)}{suffix}</span>
      </div>
      <div className="mt-2 h-3 overflow-hidden rounded-full bg-white/10">
        <div className={`h-full rounded-full ${accent}`} style={{ width }} />
      </div>
    </div>
  );
}

function CompactStat({ label, value }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/5 p-3">
      <div className="text-[10px] uppercase tracking-[0.2em] text-slate-400 sm:text-xs">{label}</div>
      <div className="mt-1.5 text-base font-semibold text-white sm:text-lg">{value}</div>
    </div>
  );
}

function MiniStat({ label, value, sub }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/5 p-3.5 sm:p-4">
      <div className="text-[10px] uppercase tracking-[0.2em] text-slate-400 sm:text-xs">{label}</div>
      <div className="mt-1.5 text-2xl font-semibold text-white sm:text-3xl">{value}</div>
      <div className="mt-1.5 text-xs text-slate-300 sm:text-sm">{sub}</div>
    </div>
  );
}

function Pm2Logs({ processes, loading }) {
  const [openProc, setOpenProc] = React.useState(null);
  const [tab, setTab] = React.useState('out');

  if (loading) {
    return (
      <div className="flex items-center gap-2 py-6 text-sm text-slate-400">
        <span className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-cyan-400 border-t-transparent" />
        Loading PM2 logs…
      </div>
    );
  }

  if (!processes || processes.length === 0) {
    return <div className="py-6 text-sm text-slate-400">No PM2 process data available.</div>;
  }

  const active = processes.find((p) => p.name === openProc) || processes[0];
  const lines = tab === 'err' ? active.errLines : active.outLines;
  const hasErrors = (active.errLines || []).length > 0;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2">
        {processes.map((proc) => {
          const isOnline = String(proc.status || '').toLowerCase() === 'online';
          const isSelected = (openProc || processes[0]?.name) === proc.name;
          return (
            <button
              key={proc.name}
              onClick={() => { setOpenProc(proc.name); setTab('out'); }}
              className={`flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold transition ${isSelected ? 'border-cyan-400/40 bg-cyan-400/15 text-cyan-100' : 'border-white/10 bg-white/5 text-slate-300 hover:bg-white/10'}`}
            >
              <span className={`inline-block h-2 w-2 rounded-full ${isOnline ? 'bg-emerald-400' : 'bg-rose-400 animate-pulse'}`} />
              {proc.name}
              {proc.pid && <span className="text-slate-500">#{proc.pid}</span>}
            </button>
          );
        })}
      </div>

      <div className="flex items-center justify-between gap-2">
        <div className="flex gap-1.5">
          <button
            onClick={() => setTab('out')}
            className={`rounded-full border px-3 py-0.5 text-xs font-semibold transition ${tab === 'out' ? 'border-cyan-400/30 bg-cyan-400/10 text-cyan-100' : 'border-white/10 bg-white/5 text-slate-400 hover:bg-white/10'}`}
          >
            stdout ({(active.outLines || []).length})
          </button>
          <button
            onClick={() => setTab('err')}
            className={`rounded-full border px-3 py-0.5 text-xs font-semibold transition ${tab === 'err' ? 'border-rose-400/30 bg-rose-400/10 text-rose-100' : hasErrors ? 'border-rose-400/20 bg-rose-400/5 text-rose-300 hover:bg-rose-400/10 animate-pulse' : 'border-white/10 bg-white/5 text-slate-400 hover:bg-white/10'}`}
          >
            stderr ({(active.errLines || []).length})
          </button>
        </div>
        <span className={`rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${String(active.status || '').toLowerCase() === 'online' ? 'border border-emerald-400/20 bg-emerald-400/10 text-emerald-200' : 'border border-rose-400/20 bg-rose-400/10 text-rose-200 animate-pulse'}`}>
          {active.status || 'unknown'}
        </span>
      </div>

      <div className="max-h-[480px] overflow-y-auto rounded-2xl border border-white/10 bg-black/40 p-3 font-mono text-[11px] leading-relaxed text-slate-300">
        {lines && lines.length > 0 ? (
          lines.map((line, i) => {
            const isError = /error|fail|exception|uncaught|fatal/i.test(line);
            const isWarn = /warn|warning/i.test(line);
            return (
              <div key={i} className={`${isError ? 'text-rose-300' : isWarn ? 'text-amber-300' : ''}`}>
                {line}
              </div>
            );
          })
        ) : (
          <span className="text-slate-500">No {tab === 'err' ? 'error' : 'output'} log lines available.</span>
        )}
      </div>
    </div>
  );
}

// ---------------------- Onboarding Tracker ----------------------

function OnboardingTracker({ companies }) {
  const [showStepRef, setShowStepRef] = useState(false);

  const enrichedCompanies = useMemo(() => {
    if (!Array.isArray(companies)) return [];

    const filtered = companies.filter(
      (c) => Number(c.Onboarded) < 18
    );

    const sorted = [...filtered].sort(
      (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
    );

    return sorted.map((company) => {
      const onboarded = Number(company.Onboarded) || 0;
      const createdAt = new Date(company.createdAt);
      const now = new Date();
      const hoursSinceCreation = (now - createdAt) / (1000 * 60 * 60);

      let trafficLight;
      if (onboarded >= 11) {
        trafficLight = 'green';
      } else if (onboarded >= 5) {
        trafficLight = 'amber';
      } else {
        if (hoursSinceCreation > 1) {
          trafficLight = 'red';
        } else {
          trafficLight = 'amber';
        }
      }

      const steps = Array.from({ length: 18 }, (_, i) => i + 1 <= onboarded);
      const currentStepNumber = onboarded < 18 ? onboarded + 1 : null;
      const currentStep = currentStepNumber
        ? STEPS_CONFIG.find((s) => s.number === currentStepNumber)
        : null;

      return {
        ...company,
        onboarded,
        createdAt,
        trafficLight,
        steps,
        currentStep,
        hasPhone: Boolean(company.Telephone),
        phone: company.Telephone || '',
      };
    });
  }, [companies]);

  const counts = useMemo(() => {
    const red = enrichedCompanies.filter((c) => c.trafficLight === 'red').length;
    const amber = enrichedCompanies.filter((c) => c.trafficLight === 'amber').length;
    const green = enrichedCompanies.filter((c) => c.trafficLight === 'green').length;
    return { red, amber, green, total: enrichedCompanies.length };
  }, [enrichedCompanies]);

  if (enrichedCompanies.length === 0) {
    return (
      <div className="rounded-2xl border border-white/10 bg-white/5 p-8 text-center text-slate-400">
        🎉 No companies in onboarding – all are fully onboarded!
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-3 text-sm">
        <span className="rounded-full border border-white/10 bg-white/5 px-3 py-1 text-slate-300">
          Total: {counts.total}
        </span>
        <span className="rounded-full border border-rose-400/30 bg-rose-400/10 px-3 py-1 text-rose-200">
          🔴 Red: {counts.red}
        </span>
        <span className="rounded-full border border-amber-400/30 bg-amber-400/10 px-3 py-1 text-amber-200">
          🟡 Amber: {counts.amber}
        </span>
        <span className="rounded-full border border-emerald-400/30 bg-emerald-400/10 px-3 py-1 text-emerald-200">
          🟢 Green: {counts.green}
        </span>
      </div>

      <div className="overflow-x-auto rounded-2xl border border-white/10 bg-white/5 p-2">
        <table className="w-full min-w-[800px] text-left text-sm">
          <thead className="text-xs uppercase text-slate-400">
            <tr>
              <th className="p-3 font-medium">Company</th>
              <th className="p-3 font-medium">Created</th>
              <th className="p-3 font-medium">Progress</th>
              <th className="p-3 font-medium">Status</th>
              <th className="p-3 font-medium">Contact</th>
            </tr>
          </thead>
          <tbody>
            {enrichedCompanies.map((company) => (
              <CompanyRow key={company.CompanyCode || company.id} company={company} />
            ))}
          </tbody>
        </table>
      </div>

      <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
        <button
          onClick={() => setShowStepRef(!showStepRef)}
          className="flex w-full items-center justify-between text-left text-sm font-semibold text-slate-300 hover:text-white"
        >
          <span>📋 Step Reference (1–18)</span>
          <span>{showStepRef ? '▲' : '▼'}</span>
        </button>
        {showStepRef && (
          <div className="mt-4 max-h-[400px] overflow-y-auto pr-2">
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {STEPS_CONFIG.map((step) => (
                <div
                  key={step.number}
                  className="rounded-xl border border-white/10 bg-black/20 p-3 text-xs"
                >
                  <div className="flex items-center gap-2">
                    <span className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-cyan-400/20 text-xs font-bold text-cyan-200">
                      {step.number}
                    </span>
                    <span className="font-medium text-white">{step.name}</span>
                    {step.route && (
                      <span className="ml-auto rounded-full border border-cyan-400/20 bg-cyan-400/10 px-2 py-0.5 text-[10px] text-cyan-300">
                        route
                      </span>
                    )}
                  </div>
                  <p className="mt-1 text-slate-400">{step.description}</p>
                  {step.requiresDPA && (
                    <span className="mt-1 inline-block rounded-full border border-amber-400/20 bg-amber-400/10 px-2 py-0.5 text-[10px] text-amber-300">
                      requires DPA
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const CompanyRow = React.memo(({ company }) => {
  const {
    CompanyName,
    createdAt,
    onboarded,
    trafficLight,
    steps,
    currentStep,
    hasPhone,
    phone,
  } = company;

  const lightColor =
    trafficLight === 'green'
      ? 'bg-emerald-400'
      : trafficLight === 'amber'
      ? 'bg-amber-400'
      : 'bg-rose-400';

  return (
    <tr className="border-t border-white/5 hover:bg-white/5">
      <td className="p-3 font-medium text-white">{CompanyName || 'Unnamed'}</td>
      <td className="p-3 text-slate-300">
        {createdAt.toLocaleDateString()} {createdAt.toLocaleTimeString()}
      </td>
      <td className="p-3">
        <div className="flex items-center gap-3">
          <div className="flex-1 min-w-[60px]">
            <div className="h-2 w-full overflow-hidden rounded-full bg-white/10">
              <div
                className="h-full rounded-full bg-gradient-to-r from-sky-400 to-cyan-300 transition-all duration-500"
                style={{ width: `${(onboarded / 18) * 100}%` }}
              />
            </div>
          </div>
          <span className="text-xs text-slate-300">
            {onboarded}/18
          </span>
        </div>
        <div className="mt-1 flex flex-wrap gap-0.5">
          {steps.map((completed, idx) => (
            <span
              key={idx}
              className={`inline-block h-2 w-2 rounded-full text-[8px] ${
                completed ? 'bg-emerald-400' : 'bg-rose-400/50'
              }`}
              title={`Step ${idx + 1}: ${completed ? '✅' : '❌'}`}
            />
          ))}
        </div>
        {currentStep && (
          <div className="mt-1.5 text-[11px] text-cyan-300">
            ⏳ Step {currentStep.number}: {currentStep.name}
          </div>
        )}
      </td>
      <td className="p-3">
        <span className={`inline-block h-3 w-3 rounded-full ${lightColor}`} />
        <span className="ml-2 capitalize text-slate-300">{trafficLight}</span>
      </td>
      <td className="p-3">
        {hasPhone ? (
          <a
            href={`tel:${phone}`}
            className="flex items-center gap-1 text-cyan-300 hover:text-cyan-100"
          >
            <span className="text-lg">📞</span>
            <span className="text-xs">{phone}</span>
          </a>
        ) : (
          <span className="text-slate-500">—</span>
        )}
      </td>
    </tr>
  );
});

// ---------------------- Live Sales Tracker ----------------------

function LiveSalesTracker({ salesLeads, markovData }) {
  const [expandedRows, setExpandedRows] = useState({});

  const liveSales = useMemo(() => {
    if (!Array.isArray(salesLeads)) return [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    return salesLeads
      .filter((lead) => {
        // Always include if TimeLeft is not null
        const hasTimeLeft = lead.TimeLeft !== null && lead.TimeLeft !== undefined;
        if (hasTimeLeft) return true;

        // Otherwise, require Live = 1 and date constraint
        if (Number(lead.Live) !== 1) return false;

        let isToday = false;
        if (lead.CreatedAt) {
          const created = new Date(lead.CreatedAt);
          if (created >= today && created < tomorrow) isToday = true;
        }
        if (!isToday && lead.UpdatedAt) {
          const updated = new Date(lead.UpdatedAt);
          if (updated >= today && updated < tomorrow) isToday = true;
        }
        return isToday;
      })
      .map((lead) => {
        const created = lead.CreatedAt ? new Date(lead.CreatedAt) : null;
        const updated = lead.UpdatedAt ? new Date(lead.UpdatedAt) : null;
        let lastActivity = null;
        let lastActivityType = '';
        if (created && updated) {
          if (updated > created) {
            lastActivity = updated;
            lastActivityType = 'Updated';
          } else {
            lastActivity = created;
            lastActivityType = 'Created';
          }
        } else if (created) {
          lastActivity = created;
          lastActivityType = 'Created';
        } else if (updated) {
          lastActivity = updated;
          lastActivityType = 'Updated';
        }
        return { ...lead, lastActivity, lastActivityType };
      })
      .sort((a, b) => {
        const aHasTime = a.TimeLeft !== null && a.TimeLeft !== undefined;
        const bHasTime = b.TimeLeft !== null && b.TimeLeft !== undefined;
        if (aHasTime && !bHasTime) return -1;
        if (!aHasTime && bHasTime) return 1;
        const dateA = a.lastActivity || new Date(0);
        const dateB = b.lastActivity || new Date(0);
        return dateB - dateA;
      });
  }, [salesLeads]);

  // Markov Insights – markovData is already the inner data object
  const markovInsights = useMemo(() => {
    if (!markovData) return null;
    return markovData;
  }, [markovData]);

  const toggleExpand = (id) => {
    setExpandedRows((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  if (liveSales.length === 0 && !markovInsights) {
    return (
      <div className="rounded-2xl border border-white/10 bg-white/5 p-8 text-center text-slate-400">
        No live sales or Markov data available.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Markov Insights Panel */}
      {markovInsights && (
        <div className="rounded-2xl border border-cyan-400/30 bg-cyan-400/5 p-4">
          <div className="flex items-center gap-2">
            <span className="text-lg">📊</span>
            <h3 className="text-sm font-semibold text-white">Markov Insights</h3>
            <span className="text-xs text-slate-400">(Total records: {markovInsights.totalRecords})</span>
          </div>
          <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {/* Main Factors */}
            {markovInsights.mainFactors && (
              <div className="rounded-xl border border-white/10 bg-black/20 p-3">
                <div className="text-xs uppercase text-slate-400">Main Factors</div>
                <div className="mt-2 space-y-1">
                  {Object.entries(markovInsights.mainFactors)
                    .slice(0, 4)
                    .map(([key, value]) => (
                      <div key={key} className="flex items-center justify-between text-xs">
                        <span className="text-slate-300">{key}</span>
                        <span className="font-medium text-cyan-300">
                          {value.total} ({value.probability}%)
                        </span>
                      </div>
                    ))}
                </div>
              </div>
            )}
            {/* Tools */}
            {markovInsights.tools && Object.keys(markovInsights.tools).length > 0 && (
              <div className="rounded-xl border border-white/10 bg-black/20 p-3">
                <div className="text-xs uppercase text-slate-400">Top Tools</div>
                <div className="mt-2 space-y-1">
                  {Object.entries(markovInsights.tools)
                    .sort((a, b) => b[1].total - a[1].total)
                    .slice(0, 4)
                    .map(([tool, data]) => (
                      <div key={tool} className="flex items-center justify-between text-xs">
                        <span className="text-slate-300">{tool}</span>
                        <span className="font-medium text-cyan-300">
                          {data.total} ({data.probability}%)
                        </span>
                      </div>
                    ))}
                </div>
              </div>
            )}
            {/* Quick stats */}
            <div className="rounded-xl border border-white/10 bg-black/20 p-3">
              <div className="text-xs uppercase text-slate-400">Highlights</div>
              <div className="mt-2 space-y-1 text-xs">
                <div className="flex justify-between">
                  <span className="text-slate-300">Approval rate</span>
                  <span className="text-emerald-300">
                    {markovInsights.mainFactors?.['All visitors']?.probability || 0}%
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-300">Top factor</span>
                  <span className="text-white">
                    {Object.entries(markovInsights.mainFactors || {})
                      .sort((a, b) => b[1].probability - a[1].probability)[0]?.[0] || '—'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-300">Total records</span>
                  <span className="text-white">{markovInsights.totalRecords}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Table header & counts */}
      <div className="flex items-center gap-3">
        <h2 className="text-lg font-semibold text-white">Live Sales</h2>
        <span className="rounded-full border border-emerald-400/30 bg-emerald-400/10 px-3 py-1 text-xs text-emerald-200">
          {liveSales.length} active
        </span>
        <span className="rounded-full border border-amber-400/30 bg-amber-400/10 px-3 py-1 text-xs text-amber-200">
          {liveSales.filter((l) => l.TimeLeft !== null && l.TimeLeft !== undefined).length} with time left
        </span>
      </div>

      {/* Table */}
      <div className="overflow-x-auto rounded-2xl border border-white/10 bg-white/5 p-2">
        <table className="w-full min-w-[1200px] text-left text-sm">
          <thead className="text-xs uppercase text-slate-400">
            <tr>
              <th className="p-3 font-medium">Location</th>
              <th className="p-3 font-medium">Visits</th>
              <th className="p-3 font-medium">Form</th>
              <th className="p-3 font-medium">Trial Click</th>
              <th className="p-3 font-medium">Price Click</th>
              <th className="p-3 font-medium">AI Tool</th>
              <th className="p-3 font-medium">Questionnaire</th>
              <th className="p-3 font-medium">Course Viewed</th>
              <th className="p-3 font-medium">Approved</th>
              <th className="p-3 font-medium">Duration</th>
              <th className="p-3 font-medium">Last Activity</th>
            </tr>
          </thead>
          <tbody>
            {liveSales.map((lead) => {
              const id = lead.id || lead._id;
              const hasForm = Boolean(lead.CompanyName);
              const visits = Number(lead.Visits) || 0;
              const returnVisitor = visits > 1;
              const trialClick = Number(lead.TrialClick) === 1;
              const priceClick = Number(lead.PriceClick) === 1;
              const aiTool = Number(lead.AIToolUsage) === 1;
              const trialApproved = Number(lead.TrialApproved) === 1;
              const score = lead.Score;
              const users = lead.Users;
              const qReport = lead.QReport;
              const hasTimeLeft = lead.TimeLeft !== null && lead.TimeLeft !== undefined;
              const duration = hasTimeLeft ? lead.Duration : null;
              let questionnaireData = null;
              if (qReport) {
                try {
                  questionnaireData = JSON.parse(qReport);
                } catch (e) {
                  questionnaireData = null;
                }
              }

              return (
                <tr
                  key={id}
                  className={`border-t border-white/5 hover:bg-white/5 ${
                    trialApproved ? 'animate-pulse bg-emerald-500/5' : ''
                  } ${hasTimeLeft ? 'bg-rose-500/20 border-rose-400/30' : ''}`}
                >
                  <td className="p-3 text-white">{lead.Location || '—'}</td>
                  <td className="p-3 text-slate-300">
                    {visits === 0 ? '—' : returnVisitor ? `Return Visitor (${visits})` : `${visits} visit`}
                  </td>
                  <td className="p-3">
                    {hasForm ? (
                      <div>
                        <div className="font-medium text-white">{lead.CompanyName}</div>
                        <div className="text-xs text-slate-400">
                          {lead.Email || 'no email'} · {lead.Industry || 'no industry'}
                        </div>
                      </div>
                    ) : (
                      <span className="text-rose-300">No form completed</span>
                    )}
                  </td>
                  <td className="p-3">
                    {trialClick ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-400/20 px-2 py-1 text-xs font-semibold text-emerald-200">
                        ✅
                      </span>
                    ) : (
                      <span className="text-slate-500">—</span>
                    )}
                  </td>
                  <td className="p-3">
                    {priceClick ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-400/20 px-2 py-1 text-xs font-semibold text-emerald-200">
                        ✅
                      </span>
                    ) : (
                      <span className="text-slate-500">—</span>
                    )}
                  </td>
                  <td className="p-3">
                    {aiTool ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-400/20 px-2 py-1 text-xs font-semibold text-emerald-200">
                        ✅
                      </span>
                    ) : (
                      <span className="text-slate-500">—</span>
                    )}
                  </td>
                  <td className="p-3">
                    {questionnaireData ? (
                      <div>
                        <button
                          onClick={() => toggleExpand(id)}
                          className="text-xs text-cyan-300 hover:text-cyan-100"
                        >
                          {expandedRows[id] ? '▲ Hide' : '▼ Show'} ({questionnaireData.length} Qs)
                        </button>
                        {expandedRows[id] && (
                          <div className="mt-2 max-h-40 overflow-y-auto rounded-lg border border-white/10 bg-black/30 p-2 text-xs text-slate-200">
                            {questionnaireData.map((q, idx) => (
                              <div key={idx} className="mb-1">
                                <strong>{q.question}</strong>
                                <span className="ml-2 text-slate-300">{q.answer}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ) : (
                      <span className="text-slate-500">—</span>
                    )}
                  </td>
                  <td className="p-3">
                    {lead.CourseViewed ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-400/20 px-2 py-1 text-xs font-semibold text-emerald-200">
                        {lead.CourseViewed}
                      </span>
                    ) : (
                      <span className="text-slate-500">—</span>
                    )}
                  </td>
                  <td className="p-3">
                    {trialApproved ? (
                      <div>
                        <span className="inline-flex items-center gap-1 rounded-full bg-emerald-400/20 px-2 py-1 text-xs font-semibold text-emerald-200">
                          ✅
                        </span>
                        {score !== undefined && score !== null && (
                          <div className="text-xs text-slate-300">Score: {score}</div>
                        )}
                        {users !== undefined && users !== null && (
                          <div className="text-xs text-slate-300">Users: {users}</div>
                        )}
                      </div>
                    ) : (
                      <span className="text-slate-500">—</span>
                    )}
                  </td>
                  <td className="p-3 text-slate-300">
                    {hasTimeLeft ? duration || '—' : '—'}
                  </td>
                  <td className="p-3 text-xs text-slate-300">
                    {lead.lastActivity ? (
                      <div>
                        <div>
                          {lead.lastActivity.toLocaleDateString()} {lead.lastActivity.toLocaleTimeString()}
                        </div>
                        <div className="text-[10px] text-slate-400">{lead.lastActivityType}</div>
                      </div>
                    ) : (
                      '—'
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}